import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { BookOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'

export type TimelineEntry = {
    id: string
    parva: number
    title: string
    body: string
    linked: boolean
}

interface EventTimelineProps {
    entries: TimelineEntry[]
    labelledBy?: string
    className?: string
}

const EventTimeline: FC<EventTimelineProps> = ({ entries, labelledBy, className }): ReactNode => {
    const { locale, t } = useLocale()
    const latin = locale === 'en'

    if (entries.length === 0) return null

    const badgeClass = cn(
        'relative inline-flex h-8 items-center gap-1.5 rounded-full border px-3 font-semibold lining-nums',
        latin ? 'text-[0.7rem] uppercase tracking-[0.14em]' : 'text-xs'
    )

    return (
        <ol role='list' aria-labelledby={labelledBy} className={cn('relative', className)}>
            {entries.map((entry, index) => {
                const last = index === entries.length - 1
                const label = `${t.parva} ${entry.parva}`

                return (
                    <li key={entry.id} className='flex gap-4 sm:gap-5'>
                        <div aria-hidden='true' className='flex w-4 shrink-0 flex-col items-center'>
                            <span className='mt-2 size-4 shrink-0 rounded-full bg-linear-to-br from-gold-soft to-gold shadow-[0_6px_14px_-6px_rgb(150_100_20/0.8)] ring-4 ring-gold/15' />

                            {!last && (
                                <span className='my-2 w-px flex-1 bg-linear-to-b from-gold/80 via-gold/40 to-gold/15' />
                            )}
                        </div>

                        <div className={cn('min-w-0 flex-1', !last && 'pb-10')}>
                            {entry.linked ? (
                                <Link
                                    href={`/parva/${entry.parva}`}
                                    className={cn(
                                        badgeClass,
                                        'border-gold/60 bg-card text-primary transition-colors before:absolute before:inset-x-0 before:-inset-y-1.5',
                                        'hover:border-primary hover:bg-primary hover:text-primary-foreground motion-reduce:transition-none',
                                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                                    )}
                                >
                                    <BookOpen className='size-4' aria-hidden='true' />
                                    {label}

                                    {entry.title && (
                                        <span className='sr-only'>
                                            : {entry.title}
                                        </span>
                                    )}
                                </Link>
                            ) : (
                                <span className={cn(badgeClass, 'border-border bg-background/70 text-muted-foreground')}>
                                    <BookOpen className='size-4' aria-hidden='true' />
                                    {label}
                                </span>
                            )}

                            {entry.title && (
                                <h4 className={cn('mt-3 font-heading text-xl font-semibold text-foreground sm:text-2xl', latin ? 'leading-tight' : 'leading-snug')}>
                                    {entry.title}
                                </h4>
                            )}

                            {entry.body && (
                                <p className={cn('mt-2 text-base text-foreground/80', latin ? 'leading-relaxed' : 'leading-[1.85]')}>
                                    {entry.body}
                                </p>
                            )}
                        </div>
                    </li>
                )
            })}
        </ol>
    )
}

export default EventTimeline
